"use client";
import { useTranslate } from "@/components/base/providers/TranslateProv";
import ArticleCard from "@/components/shared/ArticleCard";
import Loader from "@/components/shared/Loader";
import NoArticlesFound from "@/components/shared/NoArticlesFound";
import Pagination from "@/components/shared/Pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import search from "@/public/images/search.svg";
import {
  useGetAllBlogsWithFiltersQuery,
  useGetBlogCategoriesQuery,
} from "@/redux/features/api/website/websiteApiSlice";
import { Blog, type BlogListResponse } from "@/types/blog";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

type SearchFormInputs = {
  search: string;
};

const ArticlesClient = ({
  initialData,
  initialPage,
}: {
  initialData: BlogListResponse | null;
  initialPage: number;
}) => {
  const { locale } = useTranslate();
  const lang = locale as "ar" | "en";
  const router = useRouter();
  const pathname = usePathname();
  const { data: categoriesData } = useGetBlogCategoriesQuery({ locale: lang });

  const [currentPage, setCurrentPage] = useState(initialPage);
  const itemsPerPage = 9;
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("all");

  const { register, handleSubmit } = useForm<SearchFormInputs>({
    defaultValues: {
      search: searchQuery,
    },
  });

  const { currentData, isLoading, error, isFetching } =
    useGetAllBlogsWithFiltersQuery({
      search: searchQuery,
      page: currentPage,
      limit: itemsPerPage,
      category_id: selectedCategory === "all" ? undefined : selectedCategory,
      sub_category_id:
        selectedCategory === "all" || selectedSubCategory === "all"
          ? undefined
          : selectedSubCategory,
      lang,
    });

  const translations = {
    ar: {
      mainTitle: "قراءة أفضل المدونات لدينا",
      subtitle:
        "نافذتك الشاملة على أخبار الجامعات والتعليم العالي، مع مقالات وإرشادات موثوقة من سفير الجامعات.",
      searchPlaceholder: "ابحث عن مقالة معينة هنا",
      all: "الكل",
    },
    en: {
      mainTitle: "Read Our Best Blogs",
      subtitle: "Your hub for university and higher education news in Egypt",
      searchPlaceholder: "Search for a specific article here",
      all: "All",
    },
  };

  const t = translations[lang];

  const clearPaginationUrl = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("page")) {
      return;
    }

    url.searchParams.delete("page");
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  };

  const onSubmit = (data: SearchFormInputs) => {
    setSearchQuery(data.search);
    setCurrentPage(1);
    clearPaginationUrl();
  };

  const handlePageChange = (selectedItem: { selected: number }) => {
    const nextPage = selectedItem.selected + 1;
    setCurrentPage(nextPage);
    if (
      searchQuery.trim() === "" &&
      selectedCategory === "all" &&
      selectedSubCategory === "all"
    ) {
      router.push(
        nextPage === 1 ? pathname : `${pathname}?page=${nextPage}`,
        { scroll: false },
      );
    }
  };
  const resultsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentPage]);

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setSelectedSubCategory("all");
    setCurrentPage(1);
    clearPaginationUrl();
  };

  const handleSubCategoryChange = (value: string) => {
    setSelectedSubCategory(value);
    setCurrentPage(1);
    clearPaginationUrl();
  };

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    selectedCategory !== "all" ||
    selectedSubCategory !== "all";
  const canUseInitialData =
    currentPage === initialPage &&
    !hasActiveFilters;
  const displayedData =
    currentData?.data ?? (canUseInitialData ? initialData?.data : undefined);
  const articles = displayedData?.data ?? [];
  const pagination = displayedData?.paginate;
  const showLoader = (isLoading || isFetching) && articles.length === 0;
  const showError = Boolean(error) && articles.length === 0;

  return (
    <>
      <div
        className="bg-primaryColor pt-10 pb-32 flex flex-col gap-5 mb-28"
        ref={resultsRef}
      >
        <h1 className="text-36 text-white font-[500] mb-5 text-center">
          {t.mainTitle}
        </h1>
        <p className="text-18 text-white font-[500] mb-3 text-center">
          {t.subtitle}
        </p>
        <div className="relative container w-full mt-20 bg-white shadow-xl p-5 rounded-[10px] mx-auto -mb-[11rem]">
          <form onSubmit={handleSubmit(onSubmit)} className="relative">
            <input
              type="text"
              {...register("search")}
              className={`w-full h-[60px] bg-[#F8F9FA] rounded-[10px] py-3 px-14 ${
                lang === "ar" ? "text-right" : "text-left"
              }`}
              placeholder={t.searchPlaceholder}
            />
            <Image
              src={search}
              alt="search"
              className={`absolute top-1/2 -translate-y-1/2 ${lang === "ar" ? "right-5" : "left-5"}`}
            />
          </form>
        </div>
      </div>
      {showLoader && (
        <div className="flex justify-center items-center h-[300px]">
          <Loader />
        </div>
      )}
      {showError && (
        <div className="flex justify-center items-center h-[300px]">
          <p className="text-center text-20 text-red-500 font-[600]">
            {lang === "ar" ? "خطأ في جلب البيانات." : "Error fetching data."}
          </p>
        </div>
      )}
      <div className="container ">
        {/* Categories Tabs - الصف الأول */}
        <Tabs
          value={selectedCategory}
          onValueChange={handleCategoryChange}
          defaultValue="all"
          className="w-full"
        >
          <div className="overflow-x-auto scrollbar-hide">
            <TabsList
              className="flex flex-nowrap w-max sm:w-full justify-start gap-2 mb-6 min-h-[44px] p-2 bg-white border border-gray-200 rounded-lg shadow-sm"
              dir={lang === "ar" ? "rtl" : "ltr"}
            >
              <TabsTrigger
                value="all"
                className="flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2 min-h-[36px] whitespace-nowrap rounded-md transition-all duration-200 hover:bg-primaryColor hover:text-white data-[state=active]:bg-primaryColor data-[state=active]:text-white data-[state=active]:shadow-md"
              >
                {t.all}
              </TabsTrigger>
              {categoriesData?.data?.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id.toString()}
                  className="flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2 min-h-[36px] whitespace-nowrap rounded-md transition-all duration-200 hover:bg-primaryColor hover:text-white data-[state=active]:bg-primaryColor data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {/* Content for "الكل" tab */}
          <TabsContent value="all">{/* سيتم عرض كل المقالات */}</TabsContent>
        </Tabs>

        {/* Sub-Categories Tabs - الصف الثاني */}
        {selectedCategory !== "all" && (
          <Tabs
            value={selectedSubCategory}
            onValueChange={handleSubCategoryChange}
            defaultValue="all"
            className="w-full"
          >
            <div className="overflow-x-auto scrollbar-hide">
              <TabsList
                className="flex flex-nowrap w-full justify-start gap-2 mb-6 min-h-[44px] p-2 bg-white border border-gray-200 rounded-lg shadow-sm"
                dir={lang === "ar" ? "rtl" : "ltr"}
                style={{ direction: lang === "ar" ? "rtl" : "ltr" }}
              >
                <TabsTrigger
                  value="all"
                  className={`flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2 min-h-[36px] whitespace-nowrap rounded-md transition-all duration-200 hover:bg-primaryColor hover:text-white data-[state=active]:bg-primaryColor data-[state=active]:text-white data-[state=active]:shadow-md ${lang === "ar" ? "text-right" : "text-left"}`}
                >
                  {t.all}
                </TabsTrigger>{" "}
                {categoriesData?.data
                  ?.find((cat) => cat.id.toString() === selectedCategory)
                  ?.sub_categories.map((subCat) => (
                    <TabsTrigger
                      key={subCat.id}
                      value={subCat.id.toString()}
                      className={`flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2 min-h-[36px] whitespace-nowrap rounded-md transition-all duration-200 hover:bg-primaryColor hover:text-white data-[state=active]:bg-primaryColor data-[state=active]:text-white data-[state=active]:shadow-md ${lang === "ar" ? "text-right" : "text-left"}`}
                    >
                      {subCat.name}
                    </TabsTrigger>
                  ))}
              </TabsList>
            </div>
          </Tabs>
        )}
      </div>
      <div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 my-5 transition-opacity duration-300 ease-in-out">
        {showLoader && (
          <div className="flex justify-center items-center h-[200px] col-span-full">
            <Loader />
          </div>
        )}
        {!showError &&
          articles.map((blog: Blog) => (
            <ArticleCard key={blog.id} blog={blog} />
          ))}
      </div>
      {!showLoader && !showError && articles.length === 0 && (
        <NoArticlesFound />
      )}
      {(pagination?.totalPages ?? 0) > 1 && (
        <Pagination
          pageCount={pagination?.totalPages ?? 0}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          hrefBuilder={
            hasActiveFilters
              ? undefined
              : (page) =>
                  page === 1 ? pathname : `${pathname}?page=${page}`
          }
        />
      )}
    </>
  );
};

export default ArticlesClient;
